import {
  chains,
  DashboardMarketAsset,
  getChainKeyFromProtocol,
  ProtocolStateService,
} from "@silo/types";
import { MerklCampaign } from "../merkl-client/types";
import { RawDashboardMarketAsset } from "./types";
import { convertToUsd, Ltv } from "@silo/utils";
import { Effect, Option, pipe } from "effect";
import { getMerklRewardsByType } from "../utils/get-merkl-reward";

export type GetDashboardMarketAsset = ReturnType<
  typeof getDashboardMarketAsset
>;
export type GetDashboardMarketAssetSuccess =
  Effect.Effect.Success<GetDashboardMarketAsset>;
export type GetDashboardMarketAssetError =
  Effect.Effect.Error<GetDashboardMarketAsset>;
export type GetDashboardMarketAssetContext =
  Effect.Effect.Context<GetDashboardMarketAsset>;

interface GetDashboardMarketAssetParams {
  rawDashboardMarketAsset: RawDashboardMarketAsset;
  assetMerklCampaigns: MerklCampaign[];
  ltv: Ltv;
}

export function getDashboardMarketAsset({
  rawDashboardMarketAsset,
  assetMerklCampaigns,
  ltv,
}: GetDashboardMarketAssetParams) {
  return Effect.gen(function* () {
    const { collateralMerklRewards, protectedMerklRewards, debtMerklRewards } =
      getMerklRewardsByType(assetMerklCampaigns, rawDashboardMarketAsset);

    const { tokenAddress, protocolKey } = rawDashboardMarketAsset;

    const chainKey = getChainKeyFromProtocol(protocolKey);
    const { nativeCurrency } = chains[chainKey];
    const { quotePriceUsd } = yield* ProtocolStateService;

    const depositLiquidationPriceUsd = pipe(
      ltv.depositAssetsMap.get(tokenAddress),
      Option.fromNullable,
      Option.flatMapNullable((metrics) => metrics.liquidationPriceNative),
      Option.map((priceNative) =>
        convertToUsd(priceNative, nativeCurrency.decimals, quotePriceUsd)
      ),
      Option.getOrNull
    );

    const debtLiquidationPriceUsd = pipe(
      ltv.debtAssetsMap.get(tokenAddress),
      Option.fromNullable,
      Option.flatMapNullable((metrics) => metrics.liquidationPriceNative),
      Option.map((priceNative) =>
        convertToUsd(priceNative, nativeCurrency.decimals, quotePriceUsd)
      ),
      Option.getOrNull
    );

    const dashboardMarketAsset: DashboardMarketAsset = {
      ...rawDashboardMarketAsset,

      collateralMerklRewards,
      protectedMerklRewards,
      debtMerklRewards,

      depositLiquidationPriceUsd,
      debtLiquidationPriceUsd,
    };

    return dashboardMarketAsset;
  });
}
